import { IAnonymizedCheckin } from 'src/interfaces/Checkin';
import { EAgeGroups, TStatistic, TStatisticSummary } from 'src/interfaces/Statistics';
import { TechnicalType } from 'src/interfaces/TechnicalType';

type AgeGroupStats = {
  bestTime?: number;
  averageTime?: number;
  noOfParticipants?: number;
  /** @deprecated server used this name before aligning with client */
  noOfApplicants?: number;
};

type FinishTimeBucketUnit = 'minutes' | 'hours';

type FinishTimeBucketScheme = {
  unit: FinishTimeBucketUnit;
  labels: readonly string[];
  upperBoundsMinutes: readonly number[];
};

/** @deprecated use getFinishTimeBucketScheme; short-distance minute labels */
export const FINISH_TIME_BUCKET_LABELS = [
  '0-30',
  '31-60',
  '61-90',
  '91-120',
  '121-150',
  '151-180',
  '181-240',
  '241+',
] as const;

const FINISH_TIME_BUCKET_SCHEMES = {
  short: {
    unit: 'minutes' as const,
    labels: FINISH_TIME_BUCKET_LABELS,
    upperBoundsMinutes: [30, 60, 90, 120, 150, 180, 240, Number.POSITIVE_INFINITY],
  },
  medium: {
    unit: 'hours' as const,
    labels: ['0-1', '1-2', '2-3', '3-4', '4-5', '5-6', '6-8', '8+'],
    upperBoundsMinutes: [60, 120, 180, 240, 300, 360, 480, Number.POSITIVE_INFINITY],
  },
  long: {
    unit: 'hours' as const,
    labels: ['2-3', '3-4', '4-5', '5-6', '6-7', '7-8', '8-10', '10+'],
    upperBoundsMinutes: [180, 240, 300, 360, 420, 480, 600, Number.POSITIVE_INFINITY],
  },
};

const SHORT_DISTANCE_KM_MAX = 15;
const MEDIUM_DISTANCE_KM_MAX = 35;

/** Parse race length in km from distance name or i18n label (e.g. "45km", "45 km"). */
export function parseDistanceKm(label: string): number | undefined {
  const match = label.match(/(\d+)/);
  return match ? Number(match[1]) : undefined;
}

function distanceKmFromTechnicalType(
  distance: Pick<TechnicalType, 'name' | 'i18n'>,
): number | undefined {
  return (
    parseDistanceKm(distance.name) ??
    parseDistanceKm(distance.i18n.en ?? Object.values(distance.i18n)[0] ?? '')
  );
}

export function compareTechnicalTypesByDistanceKm(
  a: Pick<TechnicalType, 'name' | 'i18n'>,
  b: Pick<TechnicalType, 'name' | 'i18n'>,
): number {
  const aKm = distanceKmFromTechnicalType(a) ?? Number.POSITIVE_INFINITY;
  const bKm = distanceKmFromTechnicalType(b) ?? Number.POSITIVE_INFINITY;
  if (aKm !== bKm) return aKm - bKm;
  return a.name.localeCompare(b.name);
}

export function sortDistanceIds(
  distanceIds: readonly string[],
  distancesById?: Record<string, Pick<TechnicalType, 'name' | 'i18n'>>,
): string[] {
  return [...distanceIds].sort((aId, bId) => {
    const a = distancesById?.[aId];
    const b = distancesById?.[bId];
    if (!a && !b) return aId.localeCompare(bId);
    if (!a) return 1;
    if (!b) return -1;
    return compareTechnicalTypesByDistanceKm(a, b);
  });
}

export function distanceKmFromId(
  distanceId: string | undefined,
  distancesById?: Record<string, TechnicalType>,
): number | undefined {
  if (!distanceId || !distancesById?.[distanceId]) return undefined;
  return distanceKmFromTechnicalType(distancesById[distanceId]);
}

export function getFinishTimeBucketScheme(distanceKm?: number): FinishTimeBucketScheme {
  if (distanceKm == null || distanceKm <= SHORT_DISTANCE_KM_MAX) {
    return FINISH_TIME_BUCKET_SCHEMES.short;
  }
  if (distanceKm <= MEDIUM_DISTANCE_KM_MAX) {
    return FINISH_TIME_BUCKET_SCHEMES.medium;
  }
  return FINISH_TIME_BUCKET_SCHEMES.long;
}

function emptyBuckets(scheme: FinishTimeBucketScheme) {
  return scheme.labels.map(label => ({ label, count: 0 }));
}

function bucketsMatchScheme(
  buckets: { label: string }[],
  scheme: FinishTimeBucketScheme,
): boolean {
  return (
    buckets.length === scheme.labels.length &&
    buckets.every((bucket, index) => bucket.label === scheme.labels[index])
  );
}

export function computeFinishTimeBuckets(
  durations: number[],
  distanceKm?: number,
): { label: string; count: number }[] {
  const scheme = getFinishTimeBucketScheme(distanceKm);
  const counts = emptyBuckets(scheme);
  durations.forEach(d => {
    if (!d || d <= 0) return;
    const idx = scheme.upperBoundsMinutes.findIndex(bound => d <= bound);
    const bucket = idx >= 0 ? counts[idx] : undefined;
    if (bucket) {
      bucket.count += 1;
    }
  });
  return counts;
}

/** True when the check-in has a recorded finish timer. */
export function hasFinishTimer(row: { finishTime?: Date | string | null }): boolean {
  return row.finishTime != null && row.finishTime !== '';
}

/** DNF for stats: explicit flag, or no finish timer. */
export function isStatisticDnf(row: {
  dnf?: boolean;
  finishTime?: Date | string | null;
}): boolean {
  return Boolean(row.dnf) || !hasFinishTimer(row);
}

/** Finishers with a positive duration — used for all time metrics. */
export function timedNonDnfRows(rows: IAnonymizedCheckin[]): IAnonymizedCheckin[] {
  return rows.filter(r => !isStatisticDnf(r) && (r.duration ?? 0) > 0);
}

export function computeStatisticSummary(checkins: IAnonymizedCheckin[]): TStatisticSummary {
  const timed = timedNonDnfRows(checkins);
  const durations = timed.map(c => c.duration).filter((d): d is number => !!d && d > 0);
  const sorted = [...durations].sort((a, b) => a - b);
  const dnfCount = checkins.filter(isStatisticDnf).length;
  const totalParticipants = checkins.length - dnfCount;
  const fastestTime = sorted[0] ?? 0;
  const averageTime =
    durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;
  const medianTime =
    sorted.length > 0
      ? sorted.length % 2 === 1
        ? (sorted[Math.floor(sorted.length / 2)] ?? 0)
        : ((sorted[sorted.length / 2 - 1] ?? 0) + (sorted[sorted.length / 2] ?? 0)) / 2
      : 0;

  return {
    totalParticipants,
    dnfCount,
    fastestTime,
    medianTime,
    averageTime,
  };
}

export function getAgeGroupParticipantCount(stats?: AgeGroupStats): number {
  if (!stats) return 0;
  return stats.noOfParticipants ?? stats.noOfApplicants ?? 0;
}

/** Rebuild finish-time histogram from per-age aggregates when legacy saves omit buckets. */
function approximateFinishTimeBucketsFromAgeGroups(stat: TStatistic, distanceKm?: number) {
  const durations: number[] = [];
  stat.checkinsByGenderByAgeKeys.forEach(gender => {
    const byAge = stat.dataByGenderByAge[gender];
    if (!byAge) return;
    Object.values(EAgeGroups).forEach(ageKey => {
      const group = byAge[ageKey];
      const count = getAgeGroupParticipantCount(group);
      const time = group?.averageTime ?? 0;
      if (count > 0 && time > 0) {
        for (let i = 0; i < count; i += 1) {
          durations.push(time);
        }
      }
    });
  });
  return computeFinishTimeBuckets(durations, distanceKm);
}

export function resolveFinishTimeBuckets(stat: TStatistic, distanceKm?: number) {
  const scheme = getFinishTimeBucketScheme(distanceKm);
  const stored = stat.finishTimeBuckets;
  if (stored?.some(b => b.count > 0) && bucketsMatchScheme(stored, scheme)) {
    return stored;
  }
  const approximated = approximateFinishTimeBucketsFromAgeGroups(stat, distanceKm);
  if (approximated.some(b => b.count > 0)) {
    return approximated;
  }
  return emptyBuckets(scheme);
}

export function normalizeStatistic(stat: TStatistic, distanceKm?: number): TStatistic {
  const dataByGenderByAge = Object.fromEntries(
    Object.entries(stat.dataByGenderByAge ?? {}).map(([gender, byAge]) => [
      gender,
      Object.fromEntries(
        Object.entries(byAge ?? {}).map(([ageKey, ageStats]) => {
          const count = getAgeGroupParticipantCount(ageStats as AgeGroupStats);
          return [
            ageKey,
            {
              ...ageStats,
              noOfParticipants: count,
            },
          ];
        }),
      ),
    ]),
  ) as TStatistic['dataByGenderByAge'];

  const summary = stat.summary
    ? {
        ...stat.summary,
        dnfCount: stat.summary.dnfCount ?? 0,
      }
    : undefined;

  return {
    ...stat,
    dataByGenderByAge,
    summary,
    finishTimeBuckets: resolveFinishTimeBuckets(
      { ...stat, dataByGenderByAge, summary },
      distanceKm,
    ),
  };
}

export function enrichStatistic(
  checkins: IAnonymizedCheckin[],
  base: Omit<TStatistic, 'summary' | 'finishTimeBuckets'>,
  distanceKm?: number,
): TStatistic {
  const durations = timedNonDnfRows(checkins)
    .map(c => c.duration)
    .filter((d): d is number => !!d && d > 0);
  const km =
    distanceKm ??
    (checkins[0]?.distance ? parseDistanceKm(checkins[0].distance) : undefined);
  return {
    ...base,
    summary: computeStatisticSummary(checkins),
    finishTimeBuckets: computeFinishTimeBuckets(durations, km),
  };
}

/** Client-side rollup for edition hub KPIs when summary is missing on older saves. */
export function rollupEditionStatistics(statistics: Record<string, TStatistic> | undefined) {
  if (!statistics) {
    return {
      totalParticipants: 0,
      distanceCount: 0,
      fastestTime: 0,
      maleCount: 0,
      femaleCount: 0,
    };
  }

  let totalParticipants = 0;
  let fastestTime = 0;
  let maleCount = 0;
  let femaleCount = 0;
  const keys = Object.keys(statistics);

  keys.forEach(key => {
    const stat = statistics[key];
    if (!stat) return;
    const summary = stat.summary;
    if (summary) {
      totalParticipants += summary.totalParticipants;
      if (summary.fastestTime > 0 && (fastestTime === 0 || summary.fastestTime < fastestTime)) {
        fastestTime = summary.fastestTime;
      }
    } else {
      const male = stat.dataByGender?.M?.ratio ?? 0;
      const female = stat.dataByGender?.F?.ratio ?? 0;
      totalParticipants += male + female;
    }
    maleCount += stat.dataByGender?.M?.ratio ?? 0;
    femaleCount += stat.dataByGender?.F?.ratio ?? 0;
    if (!summary) {
      Object.values(stat.dataByGenderByAge ?? {}).forEach(byAge => {
        Object.values(byAge).forEach(age => {
          const best = age.bestTime ?? 0;
          if (best > 0 && (fastestTime === 0 || best < fastestTime)) {
            fastestTime = best;
          }
        });
      });
    }
  });

  return {
    totalParticipants,
    distanceCount: keys.length,
    fastestTime,
    maleCount,
    femaleCount,
  };
}

export function getStatisticSummary(stat: TStatistic): TStatisticSummary {
  if (stat.summary) {
    return {
      ...stat.summary,
      dnfCount: stat.summary.dnfCount ?? 0,
    };
  }
  const male = stat.dataByGender?.M?.ratio ?? 0;
  const female = stat.dataByGender?.F?.ratio ?? 0;
  let fastestTime = 0;
  Object.values(stat.dataByGenderByAge ?? {}).forEach(byAge => {
    Object.values(byAge).forEach(age => {
      const best = age.bestTime ?? 0;
      if (best > 0 && (fastestTime === 0 || best < fastestTime)) {
        fastestTime = best;
      }
    });
  });
  const totalParticipants = male + female;
  const averageTime =
    (stat.dataByGender?.M?.averageTime ?? 0) * male + (stat.dataByGender?.F?.averageTime ?? 0) * female;
  return {
    totalParticipants,
    dnfCount: 0,
    fastestTime,
    medianTime: 0,
    averageTime: totalParticipants > 0 ? averageTime / totalParticipants : 0,
  };
}
